import type { Axios } from "axios";

import type {
  MetricCardData,
  NotificationWithTemplate,
} from "../../components/Copilot/types";
import type { OpsCredentialPayload } from "../../components/CredentialsDashboard/Form/OpsCredentialsForm.zod";
import type { AddPayerCredentialsFormFields } from "../../components/CredentialsDashboard/Form/PayerCredentialsForm.zod";
import { postPatientPayment } from "../../components/PatientsDashboard/api";
import type { PostingStatusResponse } from "../../components/polling";
import type {
  BackendFilterableSearchRequest,
  BackendFilters,
  DataGridFilter,
  FilterLogicalOperator,
} from "../../utils/backendFilters";
import { stringifyValues } from "../../utils/utils";
import { Backend, getBackend } from "../apiConfig";
import { saveFile } from "../files";
import type { ClaimsFilters } from "../filters/claimFilters";

export interface AppConfig {
  featureFlags: {
    reports: boolean;
    insuranceAr: boolean;
  };
  deniseDueNowDaysThreshold: number;
  posthog: {
    analyticsEnabled: boolean;
    replayEnabled: boolean;
    replayBaselineSampleRate: number;
    replayExcludedOrgIds: string[];
  };
  unpostBlockedPracticeIds: string[];
}

export type EvalCaseStatus = "REVIEW" | "ACCEPTED" | "EXCLUDED";

export interface VerificationEvalCase {
  canFlag: boolean;
  caseId: string | null;
  status: EvalCaseStatus | null;
}

export interface InsuranceArSearchOptions {
  practiceIds: string[];
  tab: string;
  page: number;
  pageSize: number;
  sortField?: string | null;
  sortDirection?: "asc" | "desc";
  additionalFilters?: BackendFilters;
  exportMode?: boolean;
  signal?: AbortSignal;
}

export interface AgentConfigResponse {
  agent: string;
  version: string | null;
  config: Record<string, unknown>;
  scope: string;
  activeFrom: string;
}

class QueryClient {
  private api: { getClient: () => Promise<Axios> };

  private devMode: boolean;

  constructor(devMode: boolean, api: { getClient: () => Promise<Axios> }) {
    this.devMode = devMode;
    this.api = api;
  }

  reviewStatusFilter(): string[] | undefined {
    if (!this.devMode) {
      return ["ACCEPTED", "BYPASS"];
    }
    // We hide more claims in devmode for production to reduce the noise for
    // the operations team when reviewing claims.
    if (this.devMode && getBackend() === Backend.Production) {
      return ["ACCEPTED", "BYPASS", "REVIEW", "MODIFIED"];
    }
    return undefined;
  }

  async getPatientClaims(
    patientId: string,
  ): Promise<PatientWithClaimMessage | undefined> {
    const client = await this.api.getClient();
    const response = await client.get<PatientWithClaimMessage>(
      `/patients/${patientId}`,
    );
    return response.data;
  }

  private static insuranceArRequestBody({
    practiceIds,
    page = 1,
    pageSize = 1,
    sortField,
    sortDirection,
    additionalFilters,
    exportMode,
  }: Omit<InsuranceArSearchOptions, "tab" | "signal" | "page" | "pageSize"> & {
    page?: number;
    pageSize?: number;
  }): BackendFilterableSearchRequest {
    const requestBody: BackendFilterableSearchRequest = {
      page,
      page_size: pageSize,
      sort: sortField ? [sortField] : [],
      sort_direction: sortDirection ?? "desc",
      filters: {
        ...additionalFilters?.filters,
        ...(practiceIds.length ? { practice_id: practiceIds } : {}),
      },
    };
    if (exportMode) {
      requestBody.export_mode = true;
    }
    if (additionalFilters?.datagridFilters?.length) {
      requestBody.datagrid_filters = additionalFilters.datagridFilters;
    }
    if (additionalFilters?.logicalOperator) {
      requestBody.logical_operator = additionalFilters.logicalOperator;
    }
    return requestBody;
  }

  async searchInsuranceAr({
    practiceIds,
    tab,
    page,
    pageSize,
    sortField,
    sortDirection,
    additionalFilters,
    exportMode,
    signal,
  }: InsuranceArSearchOptions): Promise<InsuranceArSearchResponse> {
    const client = await this.api.getClient();
    const requestBody = QueryClient.insuranceArRequestBody({
      practiceIds,
      page,
      pageSize,
      sortField,
      sortDirection,
      additionalFilters,
      exportMode,
    });
    const response = await client.post<InsuranceArSearchResponse>(
      `/insurance-ar/search?tab=${tab}`,
      requestBody,
      { signal },
    );
    return response.data;
  }

  async getInsuranceArOverview(
    practiceIds: string[],
    signal?: AbortSignal,
  ): Promise<InsuranceArOverviewMessage> {
    const client = await this.api.getClient();
    const response = await client.post<InsuranceArOverviewMessage>(
      "/insurance-ar/overview",
      QueryClient.insuranceArRequestBody({ practiceIds }),
      { signal },
    );
    return response.data;
  }

  async getInsuranceArActionOptions(
    signal?: AbortSignal,
  ): Promise<InsuranceArActionOptionsMessage> {
    const client = await this.api.getClient();
    const response = await client.get<InsuranceArActionOptionsMessage>(
      "/insurance-ar/action-options",
      { signal },
    );
    return response.data;
  }

  async uploadInsuranceArReport(
    practiceId: string,
    source: string,
    file: File,
  ): Promise<InsuranceArReportIngestResponseMessage> {
    const client = await this.api.getClient();
    const formData = new FormData();
    formData.append("file", file);
    formData.append("practice_id", practiceId);
    formData.append("source", source);
    const response = await client.post<InsuranceArReportIngestResponseMessage>(
      "/insurance-ar/report",
      formData,
      { headers: { "Content-Type": "multipart/form-data" } },
    );
    return response.data;
  }

  private async postUpdate(
    body: Record<string, unknown>,
    opts?: { sync?: boolean },
  ): Promise<string> {
    const { search } = window.location;
    const client = await this.api.getClient();
    const response = await client.post(
      `updates${search}`,
      opts?.sync ? { ...body, sync: true } : body,
    );
    return response.data.wieldyId;
  }

  async updateInsuranceArEntry(
    payload: InsuranceArEntryUpdatePayload,
  ): Promise<string> {
    return this.postUpdate(
      {
        type: "InsuranceArEntry",
        payload,
        verb: "UPDATE",
      },
      { sync: true },
    );
  }

  async createInsuranceArComment(
    payload: InsuranceArCommentCreatePayload,
  ): Promise<string> {
    return this.postUpdate(
      {
        type: "InsuranceArComment",
        payload,
        verb: "CREATE",
      },
      { sync: true },
    );
  }

  async updateInsuranceArComment(
    payload: InsuranceArCommentUpdatePayload,
  ): Promise<string> {
    return this.postUpdate(
      {
        type: "InsuranceArComment",
        payload,
        verb: "UPDATE",
      },
      { sync: true },
    );
  }

  async deleteInsuranceArComment(
    payload: InsuranceArCommentDeletePayload,
  ): Promise<string> {
    return this.postUpdate(
      {
        type: "InsuranceArComment",
        payload,
        verb: "DELETE",
      },
      { sync: true },
    );
  }

  async getPatientPaymentPlans(
    patientId: string,
  ): Promise<PaymentPlanMessage[] | undefined> {
    const client = await this.api.getClient();

    const response = await client.get<{ paymentPlans: PaymentPlanMessage[] }>(
      `/patient/${patientId}/payment-plans`,
    );
    return response.data.paymentPlans;
  }

  async getPatientPaymentPlan(paymentPlanId: string): Promise<Blob> {
    const client = await this.api.getClient();

    const response = await client.get<Blob>(`/payment-plans/${paymentPlanId}`, {
      responseType: "blob",
      headers: {
        accept: "application/pdf",
      },
    });
    return response.data;
  }

  async cancelPatientPaymentPlans(paymentPlanId: string): Promise<void> {
    const client = await this.api.getClient();

    await client.post(`/payment-plans/${paymentPlanId}/cancellation`);
  }

  async sentPatientPaymentPlanViaSms(paymentPlanId: string): Promise<void> {
    const client = await this.api.getClient();

    await client.post(`/payment-plans/${paymentPlanId}/notify/sms`);
  }

  async getPatientPayments(
    patientId: string,
  ): Promise<PatientPaymentMessage[] | undefined> {
    const client = await this.api.getClient();

    const response = await client.get<PatientPaymentMessage[]>(
      `patient/${patientId}/payment-methods`,
    );
    return response.data;
  }

  async getPublicPatientPayments(
    hash: string,
  ): Promise<PatientPaymentMessage[] | undefined> {
    const client = await this.api.getClient();
    const response = await client.get<PatientPaymentMessage[]>(
      `public-patient/${hash}/payment-methods`,
    );
    return response.data;
  }

  async postPatientPayment(
    patientId: string,
    token: string,
  ): Promise<PatientPaymentMessage> {
    return postPatientPayment(await this.api.getClient(), patientId, token);
  }

  async postPublicPatientPayment(
    hash: string,
    token: string,
  ): Promise<PatientPaymentMessage> {
    const client = await this.api.getClient();
    const response = await client.post<PatientPaymentMessage>(
      `/public-patient/${hash}/payment-methods`,
      {
        token,
      },
    );
    return response.data;
  }

  async postPatientPaymentPlanUnsignedPdf(
    patientId: string,
    paymentPlanMessage: PaymentPlanMessage & { signatureDate: string },
  ): Promise<Blob> {
    const client = await this.api.getClient();
    const response = await client.post<Blob>(
      `patient/${patientId}/unsigned-payment-plan`,
      paymentPlanMessage,
      { responseType: "blob" },
    );
    return response.data;
  }

  async postPatientPaymentMethod(
    patientId: string,
    paymentPlanMessage: PaymentPlanMessage & {
      signatureDate: string;
      signature: string;
    },
  ): Promise<PaymentPlanMessage> {
    const client = await this.api.getClient();
    const response = await client.post<PaymentPlanMessage>(
      `patient/${patientId}/payment-plans`,
      paymentPlanMessage,
    );
    return response.data;
  }

  async setPublicPatientPrimaryPaymentMethod(
    hash: string,
    paymentMethodId: string,
  ): Promise<object> {
    const client = await this.api.getClient();
    const response = await client.patch<object>(`public-patient/${hash}`, {
      primaryPaymentMethodId: paymentMethodId,
    });
    return response.data;
  }

  async deletePublicPaymentMethod(
    hash: string,
    paymentMethodId: string,
  ): Promise<object> {
    const client = await this.api.getClient();
    const response = await client.delete<object>(
      `public-patient/${hash}/payment-methods/${paymentMethodId}`,
    );
    return response.data;
  }

  async getClaim(
    claimId: string,
  ): Promise<ClaimWithProcedureAndPatientMessage> {
    const client = await this.api.getClient();
    const response = await client.get<ClaimWithProcedureAndPatientMessage>(
      `/claims/${claimId}?dev_mode=${this.devMode}`,
    );
    return response.data;
  }

  async resetClaim(claimId: string): Promise<void> {
    const client = await this.api.getClient();
    await client.post(`/claims/${claimId}/reset`);
  }

  async getClaimsProceduresAndPatient(
    claimIds: string[],
    page = 1,
    signal?: AbortSignal,
    additionalFilters?: BackendFilters,
    tab?: string | null,
    pageSize = 500,
    sortField?: string | null,
    sortDirection?: "asc" | "desc",
    view?: string | null,
    deniseQueue?: string | null,
  ): Promise<SearchResponse> {
    const client = await this.api.getClient();

    const filters: Record<string, string[]> = {
      ...additionalFilters?.filters,
    };
    if (claimIds.length) {
      filters.wieldy_id = claimIds;
    }

    const getSort = (): string[] => {
      if (sortField) return [sortField];
      if (sortField === null) return [];
      return ["posted_datetime", "urgency", "first_retrieved_date"];
    };

    const requestBody: BackendFilterableSearchRequest = {
      page,
      page_size: pageSize,
      sort: getSort(),
      sort_direction: sortDirection ?? "desc",
      filters,
    };

    if (additionalFilters?.datagridFilters?.length) {
      requestBody.datagrid_filters = additionalFilters.datagridFilters;
    }

    if (additionalFilters?.logicalOperator) {
      requestBody.logical_operator = additionalFilters.logicalOperator;
    }

    const hasContextFilters =
      requestBody.filters?.wieldy_id ||
      requestBody.filters?.patient_key ||
      requestBody.filters?.payment_key;

    const queryParams = new URLSearchParams();
    if (tab && !hasContextFilters) {
      queryParams.append("tab", tab);
    }
    if (view) {
      queryParams.append("view", view);
    }
    if (deniseQueue) {
      queryParams.append("denise_queue", deniseQueue);
    }
    if (this.devMode) {
      queryParams.append("dev_mode", "true");
    }

    const url = `/claims/search${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;

    const response = await client.post<SearchResponse>(url, requestBody, {
      signal,
    });
    return response.data;
  }

  async fetchClaimsPostingStatus(
    wieldyIds: string[],
    since: string | null,
    signal?: AbortSignal,
  ): Promise<PostingStatusResponse> {
    const client = await this.api.getClient();
    const response = await client.post<PostingStatusResponse>(
      "/claims/posting-status",
      { wieldyIds, since },
      { signal },
    );
    return response.data;
  }

  async fetchIVPostingStatus(
    wieldyIds: string[],
    since: string | null,
    signal?: AbortSignal,
  ): Promise<PostingStatusResponse> {
    const client = await this.api.getClient();
    const response = await client.post<PostingStatusResponse>(
      `insurance-verification/posting-status`,
      { wieldyIds, since },
      { signal },
    );
    return response.data;
  }

  async getClaimsProceduresAndPatientForExport(
    claimIds: string[],
    signal?: AbortSignal,
    additionalFilters?: BackendFilters,
    tab?: string | null,
    sortField?: string | null,
    sortDirection?: "asc" | "desc",
  ): Promise<SearchResponse> {
    const client = await this.api.getClient();

    const filters: Record<string, string[]> = {
      ...additionalFilters?.filters,
    };
    if (claimIds.length) {
      filters.wieldy_id = claimIds;
    }

    const requestBody: BackendFilterableSearchRequest = {
      page: 1,
      page_size: 10,
      sort: sortField
        ? [sortField]
        : ["posted_datetime", "urgency", "first_retrieved_date"],
      sort_direction: sortDirection ?? "desc",
      export_mode: true,
      filters,
    };

    if (additionalFilters?.datagridFilters?.length) {
      requestBody.datagrid_filters = additionalFilters.datagridFilters;
    }

    if (additionalFilters?.logicalOperator) {
      requestBody.logical_operator = additionalFilters.logicalOperator;
    }

    const hasContextFilters =
      requestBody.filters?.wieldy_id ||
      requestBody.filters?.patient_key ||
      requestBody.filters?.payment_key;

    const queryParams = new URLSearchParams();
    if (tab && !hasContextFilters) {
      queryParams.append("tab", tab);
    }
    if (this.devMode) {
      queryParams.append("dev_mode", "true");
    }

    const url = `/claims/search${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;

    const response = await client.post<SearchResponse>(url, requestBody, {
      signal,
    });
    return response.data;
  }

  async validateEntity(key: string, entityType: string): Promise<string> {
    const client = await this.api.getClient();
    const response = await client.post(`validations`, {
      key,
      entityType,
    });
    return response.data.reviewStatus;
  }

  async updateProcedure(procedure: Partial<ProcedureMessage>): Promise<string> {
    return this.postUpdate({
      type: "Procedure",
      payload: stringifyValues(procedure),
      verb: "UPDATE",
    });
  }

  async createProcedure(procedure: ProcedureMessage): Promise<string> {
    return this.postUpdate({
      type: "Procedure",
      payload: stringifyValues(procedure),
      verb: "CREATE",
    });
  }

  async deleteProcedure(
    procedure: ProcedureMessage,
    practiceId: string,
  ): Promise<string> {
    return this.postUpdate({
      type: "Procedure",
      payload: {
        wieldyId: procedure.wieldyId,
        practiceId,
      },
      verb: "DELETE",
    });
  }

  async deleteClaim(
    claim: ClaimWithProcedureAndPatientMessage,
  ): Promise<string> {
    return this.postUpdate({
      type: "Claim",
      payload: { wieldyId: claim.wieldyId, practiceId: claim.practiceId },
      verb: "DELETE",
    });
  }

  async postClaimToPMS(claimId: string): Promise<PostingStatus> {
    const { search } = window.location;
    const client = await this.api.getClient();
    const response = await client.post(`pms/${claimId}${search}`, { claimId });
    return response.data;
  }

  async postInsuranceVerificationToPMS(
    verificationId: string,
  ): Promise<PostingStatus> {
    const { search } = window.location;
    const client = await this.api.getClient();
    const url = `insurance-verification/${verificationId}/post${search}`;
    const response = await client.post(url, { verificationId });
    return response.data;
  }

  async resetInsuranceVerification(
    verificationId: string,
  ): Promise<InsuranceVerificationPostedResult | UnpostFailure> {
    const client = await this.api.getClient();
    const response = await client.post<
      InsuranceVerificationPostedResult | UnpostFailure
    >(`insurance-verification/${verificationId}/reset`);
    return response.data;
  }

  async saveFile(
    practiceId: string,
    source: string,
    file: File,
  ): Promise<FileMessage> {
    return saveFile(await this.api.getClient(), practiceId, source, file);
  }

  async updateInsuranceVerification(
    payload: InsuranceVerificationUpdateMessage,
  ): Promise<string> {
    return this.postUpdate({
      type: "InsuranceVerification",
      payload,
      verb: "UPDATE",
    });
  }

  async updatePatientInsuranceVerification(
    payload: PatientInsuranceVerificationUpdateMessage,
  ): Promise<string> {
    return this.postUpdate({
      type: "PatientInsuranceVerification",
      payload,
      verb: "UPDATE",
    });
  }

  async submitAgentActionFeedback(
    payload: AgentActionFeedbackCreate,
  ): Promise<void> {
    const client = await this.api.getClient();
    await client.post("agent-action-feedback", payload);
  }

  async verifyInsurance(request: VerifyInsuranceRequest): Promise<void> {
    const client = await this.api.getClient();
    await client.post("insurance/verify", request);
  }

  async mapInsuranceVerificationPayer(
    wieldyId: string,
    payerId: string,
  ): Promise<void> {
    const client = await this.api.getClient();
    await client.post(`insurance-verification/${wieldyId}/map-payer`, {
      payerId,
    });
  }

  async getInsuranceVerificationDetail(
    verificationId: string,
  ): Promise<InsuranceVerificationMessage> {
    const client = await this.api.getClient();
    const { search } = window.location;
    const response = await client.get<InsuranceVerificationMessage>(
      `insurance-verification/${verificationId}${search}`,
    );
    return response.data;
  }

  async getVerificationEvalCase(
    wieldyId: string,
  ): Promise<VerificationEvalCase> {
    const client = await this.api.getClient();
    const response = await client.get<VerificationEvalCase>(
      `insurance-verification/${wieldyId}/eval-case`,
    );
    return response.data;
  }

  async addVerificationEvalCase(
    wieldyId: string,
  ): Promise<VerificationEvalCase> {
    const client = await this.api.getClient();
    const response = await client.post<VerificationEvalCase>(
      `insurance-verification/${wieldyId}/eval-case`,
    );
    return response.data;
  }

  async getBennySuggestions(
    insuranceKey: string,
    patientKey?: string | null,
  ): Promise<PayerSuggestionsResponse> {
    const client = await this.api.getClient();
    const searchParams = new URLSearchParams();
    if (patientKey) {
      searchParams.set("patient_key", patientKey);
    }
    const query = searchParams.toString();
    const response = await client.get<PayerSuggestionsResponse>(
      `insurance/${insuranceKey}/benny-suggestions${query ? `?${query}` : ""}`,
    );
    return response.data;
  }

  async getPractices(): Promise<PracticeMessage[]> {
    const client = await this.api.getClient();
    const response = await client.get<PracticeMessage[]>("/practices");
    return response.data;
  }

  async getCredentialsSupportedPayers(): Promise<
    CredentialsSupportedPayersMessage[] | undefined
  > {
    const client = await this.api.getClient();

    const response = await client.get<CredentialsSupportedPayersMessage[]>(
      `/credentials/supported-payers`,
    );
    return response.data ?? [];
  }

  async getSupportedSources(): Promise<SourceMessage[] | undefined> {
    const client = await this.api.getClient();

    const response = await client.get<SourceMessage[]>(`/sources/`);
    return response.data ?? [];
  }

  async getSupportedPracticeManagementSystems(): Promise<
    PMSMessage[] | undefined
  > {
    const client = await this.api.getClient();

    const response = await client.get<PMSMessage[]>(
      `/practice-management-systems/`,
    );
    return response.data ?? [];
  }

  async getUserCredentials(
    page = 1,
    filters: Record<string, (string | null)[]> = {},
    pageSize = 500,
    signal?: AbortSignal,
    tab?: string,
    options?: {
      sortField?: string;
      sortDirection?: "asc" | "desc";
      datagridFilters?: DataGridFilter[];
      logicalOperator?: FilterLogicalOperator;
    },
  ): Promise<SearchResponse> {
    const client = await this.api.getClient();
    const body: Record<string, unknown> = {
      filters,
      page,
      page_size: pageSize,
      sort: [options?.sortField ?? "updated_at"],
      sort_direction: options?.sortDirection ?? "desc",
    };
    if (tab) {
      body.tab = tab;
    }
    if (options?.datagridFilters?.length) {
      body.datagrid_filters = options.datagridFilters;
    }
    if (options?.logicalOperator) {
      body.logical_operator = options.logicalOperator;
    }
    const response = await client.post<SearchResponse>("/credentials/", body, {
      signal,
    });
    return response.data;
  }

  async oauthEmail(): Promise<AuthEmailMessage> {
    const client = await this.api.getClient();
    const response = await client.get<AuthEmailMessage>(`/access/authorize`);
    return response.data;
  }

  async unmaskSourceAttribute(
    wieldyId: string,
    name: string,
  ): Promise<Partial<UserCredentialsMessage>> {
    const client = await this.api.getClient();
    const response = await client.get(
      `/credentials/payers/${wieldyId}/unmask?name=${name}`,
    );
    return response.data;
  }

  async getPayerCredentialTotp(wieldyId: string): Promise<TotpMessage> {
    const client = await this.api.getClient();
    const response = await client.get<TotpMessage>(
      `/credentials/payers/${wieldyId}/totp`,
    );
    return response.data;
  }

  async getPayerCredentialEmailMfa(wieldyId: string): Promise<EmailMfaMessage> {
    const client = await this.api.getClient();
    const response = await client.get<EmailMfaMessage>(
      `/credentials/payers/${wieldyId}/email-mfa`,
    );
    return response.data;
  }

  async unmaskPMSAttribute(
    wieldyId: string,
    name: string,
  ): Promise<Partial<UserCredentialsMessage>> {
    const client = await this.api.getClient();
    const response = await client.get(
      `/credentials/pms/${wieldyId}/unmask?name=${name}`,
    );
    return response.data;
  }

  async postCreateCredentials({
    type,
    sourceId,
    practiceId,
    username,
    password,
    website,
    notes,
    mfaJson,
    userType,
    userId,
  }: {
    type: AddPayerCredentialsFormFields["type"];
    sourceId: SourceMessage["id"];
    practiceId: PracticeMessage["wieldyId"];
    username: UserCredentialsMessage["username"];
    password: UserCredentialsMessage["password"];
    website?: string;
    notes?: string;
    mfaJson?: string;
    userType?: string;
    userId?: string;
  }): Promise<void> {
    const client = await this.api.getClient();
    // We don't have access to portalId yet, so we use a placeholder till we
    // have access.
    await client.post(
      `/credentials/sources/${sourceId}/practices/${practiceId}`,
      {
        type,
        username,
        password,
        website,
        notes,
        mfaJson,
        userType,
        userId,
      },
    );
  }

  async postTotpPreview(setupKey: string): Promise<TotpMessage> {
    const client = await this.api.getClient();
    const response = await client.post<TotpMessage>(
      `/credentials/totp/preview`,
      { setupKey },
    );
    return response.data;
  }

  async postCreatePMSCredentials({
    type,
    pmsId,
    practiceId,
    username,
    password,
    website,
    activationCode,
    serialNumber,
    customerNumber,
    machineIdentifier,
    userType,
    userId,
  }: {
    type: AddPayerCredentialsFormFields["type"];
    pmsId: PMSMessage["id"];
    practiceId: PracticeMessage["wieldyId"];
    username: UserCredentialsMessage["username"];
    password: UserCredentialsMessage["password"];
    website: string;
    activationCode?: string;
    serialNumber?: string;
    customerNumber?: string;
    machineIdentifier?: string;
    userType?: string;
    userId?: string;
  }): Promise<void> {
    const metaData =
      activationCode && serialNumber && customerNumber && machineIdentifier
        ? { activationCode, serialNumber, customerNumber, machineIdentifier }
        : undefined;
    const client = await this.api.getClient();
    await client.post(`/credentials/pms/${pmsId}/practices/${practiceId}`, {
      type,
      username,
      password,
      website,
      metaData,
      userType,
      userId,
    });
  }

  async postOtherCredentials({
    type,
    practiceId,
    name,
    username,
    password,
    website,
    notes,
    userType,
    userId,
  }: {
    type: string;
    practiceId: PracticeMessage["wieldyId"];
    name: string;
    username: UserCredentialsMessage["username"];
    password: UserCredentialsMessage["password"];
    website?: string;
    notes?: string;
    userType?: string;
    userId?: string;
  }): Promise<void> {
    const client = await this.api.getClient();
    await client.post(`/credentials/other/practices/${practiceId}`, {
      type,
      name,
      username,
      password,
      website,
      notes,
      userType,
      userId,
    });
  }

  async deletePMSCredential(
    credentialId: UserCredentialsMessage["wieldyId"],
  ): Promise<void> {
    const client = await this.api.getClient();
    await client.delete(`/credentials/pms/${credentialId}`);
  }

  async deletePayerCredential(
    credentialId: UserCredentialsMessage["wieldyId"],
  ): Promise<void> {
    const client = await this.api.getClient();
    await client.delete(`/credentials/payers/${credentialId}`);
  }

  async deleteOtherCredential(
    credentialId: UserCredentialsMessage["wieldyId"],
  ): Promise<void> {
    const client = await this.api.getClient();
    await client.delete(`/credentials/other/${credentialId}`);
  }

  async updateCreateCredentials({
    credentialId,
    ...body
  }: {
    credentialId: UserCredentialsMessage["wieldyId"];
    username?: UserCredentialsMessage["username"];
    password?: UserCredentialsMessage["password"];
    website?: string;
    notes?: string;
    userType?: string;
    userId?: string;
  }): Promise<void> {
    const client = await this.api.getClient();
    await client.patch(`/credentials/payers/${credentialId}`, body);
  }

  async patchPayerCredentialStatus(
    credentialId: UserCredentialsMessage["wieldyId"],
    status: "ACTIVE" | "INACTIVE",
  ): Promise<void> {
    const client = await this.api.getClient();
    await client.patch(`/credentials/payers/${credentialId}`, { status });
  }

  async updatePMSCredentials({
    credentialId,
    ...body
  }: {
    credentialId: UserCredentialsMessage["wieldyId"];
    username?: UserCredentialsMessage["username"];
    password?: UserCredentialsMessage["password"];
    website?: string;
    activationCode?: string;
    serialNumber?: string;
    customerNumber?: string;
    machineIdentifier?: string;
    userType?: string;
    userId?: string;
  }): Promise<void> {
    const { activationCode, serialNumber, customerNumber, machineIdentifier } =
      body;
    const metaData =
      activationCode && serialNumber && customerNumber && machineIdentifier
        ? { activationCode, serialNumber, customerNumber, machineIdentifier }
        : undefined;
    const client = await this.api.getClient();
    await client.patch(`/credentials/pms/${credentialId}`, {
      username: body.username,
      password: body.password,
      website: body.website,
      metaData,
      userType: body.userType,
      userId: body.userId,
    });
  }

  async updateOtherCredentials({
    credentialId,
    ...body
  }: {
    credentialId: UserCredentialsMessage["wieldyId"];
    username?: UserCredentialsMessage["username"];
    password?: UserCredentialsMessage["password"];
    website?: string;
    notes?: string;
    userType?: string;
    userId?: string;
  }): Promise<void> {
    const client = await this.api.getClient();
    await client.patch(`/credentials/other/${credentialId}`, body);
  }

  async createOpsCredential(
    practiceId: PracticeMessage["wieldyId"],
    body: OpsCredentialPayload,
  ): Promise<void> {
    const client = await this.api.getClient();
    await client.post(`/credentials/ops/practices/${practiceId}`, body);
  }

  async patchOpsCredential(
    credentialId: UserCredentialsMessage["wieldyId"],
    body: Partial<OpsCredentialPayload>,
  ): Promise<void> {
    const client = await this.api.getClient();
    await client.patch(`/credentials/ops/${credentialId}`, body);
  }

  async getOpsTargetOptions(): Promise<string[]> {
    const client = await this.api.getClient();
    const response = await client.get<string[]>("/credentials/ops/targets");
    return response.data;
  }

  async unmaskOpsAttribute(
    credentialId: UserCredentialsMessage["wieldyId"],
    name: string,
  ): Promise<Record<string, string>> {
    const client = await this.api.getClient();
    const response = await client.get(
      `/credentials/ops/${credentialId}/unmask?name=${name}`,
    );
    return response.data;
  }

  async getOpsCredentialTotp(
    credentialId: UserCredentialsMessage["wieldyId"],
  ): Promise<TotpMessage> {
    const client = await this.api.getClient();
    const response = await client.get<TotpMessage>(
      `/credentials/ops/${credentialId}/totp`,
    );
    return response.data;
  }

  async deleteOpsCredential(
    credentialId: UserCredentialsMessage["wieldyId"],
  ): Promise<void> {
    const client = await this.api.getClient();
    await client.delete(`/credentials/ops/${credentialId}`);
  }

  async getScreenshots(claimId: string): Promise<string[]> {
    const client = await this.api.getClient();
    const response = await client.get(`claims/${claimId}/screenshots`);
    return response.data;
  }

  async getInsuranceVerificationScreenshots(
    wieldyId: string,
  ): Promise<string[]> {
    const client = await this.api.getClient();
    const response = await client.get(
      `insurance-verification/${wieldyId}/screenshots/`,
    );
    return response.data;
  }

  async getBennyScreenshots(wieldyId: string): Promise<string[]> {
    const client = await this.api.getClient();
    const response = await client.get(
      `insurance-verification/${wieldyId}/benny-screenshots`,
    );
    return response.data;
  }

  async getTasks(additionalFilters: ClaimsFilters = {}): Promise<Task[]> {
    const client = await this.api.getClient();
    const filters: ClaimsFilters = {
      review_status: this.reviewStatusFilter(),
      ...additionalFilters,
    };

    const response = await client.post<Task[]>("tasks/", {
      filters,
    });
    return response.data;
  }

  async completeTask(taskId: string): Promise<Task> {
    const client = await this.api.getClient();
    const response = await client.patch<Task>(`tasks/${taskId}/complete`);
    return response.data;
  }

  async getMetabaseToken(practices: string[]): Promise<MetabaseToken> {
    const client = await this.api.getClient();
    const response = await client.get<MetabaseToken>(`/reports/metabase/sign`, {
      params: {
        practices,
      },
    });
    return response.data;
  }

  async getOrganization(): Promise<OrganizationMessage> {
    const client = await this.api.getClient();
    const response = await client.get<OrganizationMessage>(`/organizations/`);
    return response.data;
  }

  async getSwitchableOrganizations(): Promise<SwitchableOrganization[]> {
    const client = await this.api.getClient();
    const response = await client.get<SwitchableOrganization[]>(
      `/organizations/switchable`,
    );
    return response.data;
  }

  async getClaimEnumFilters(
    fields: string[],
    additionalFilters: ClaimsFilters = {},
  ): Promise<Record<string, string[]>> {
    const filters: ClaimsFilters = {
      ...additionalFilters,
    };
    const client = await this.api.getClient();

    const queryParams = new URLSearchParams();
    if (this.devMode) {
      queryParams.append("dev_mode", "true");
    }

    const url = `/claims/filter-options${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;

    const response = await client.post<{
      options: Record<string, string[]>;
    }>(url, { fields, filters });
    return response.data.options;
  }

  async getPaymentEnumFilters(
    fields: string[],
    additionalFilters: Record<string, (string | null)[]> = {},
  ): Promise<Record<string, string[]>> {
    const filters = {
      ...additionalFilters,
    };
    const client = await this.api.getClient();

    const queryParams = new URLSearchParams();
    if (this.devMode) {
      queryParams.append("dev_mode", "true");
    }

    const url = `/payments/filter-options${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;

    const response = await client.post<{
      options: Record<string, string[]>;
    }>(url, { fields, filters });
    return response.data.options;
  }

  async getAgentMetrics(
    practiceIds: string[],
  ): Promise<{ cards: MetricCardData[] }> {
    const client = await this.api.getClient();
    const response = await client.post<{ cards: MetricCardData[] }>(
      "/tasks/agent-metrics",
      { practice_ids: practiceIds },
    );
    return response.data;
  }

  async getNotifications(
    practiceIds: string[],
  ): Promise<NotificationWithTemplate[]> {
    const client = await this.api.getClient();
    const response = await client.post<NotificationWithTemplate[]>(
      "/notifications",
      {
        practiceIds,
        devMode: this.devMode,
      },
    );
    return response.data;
  }

  async handleNotificationAction(
    practiceIds: string[],
    agent?: string,
    code?: string,
  ): Promise<{
    updatedCount: number;
    completedCount: number;
    readCount: number;
  }> {
    const client = await this.api.getClient();
    const response = await client.post<{
      updatedCount: number;
      completedCount: number;
      readCount: number;
    }>("/notifications/action", {
      practiceIds,
      agent,
      code,
    });
    return response.data;
  }

  async dismissNotification(
    practiceIds: string[],
    agent?: string,
    code?: string,
  ): Promise<{ updatedCount: number }> {
    const client = await this.api.getClient();
    const response = await client.post<{ updatedCount: number }>(
      "/notifications/dismiss",
      {
        practiceIds,
        agent,
        code,
      },
    );
    return response.data;
  }

  async getAppConfig(): Promise<AppConfig> {
    const client = await this.api.getClient();
    const response = await client.get<AppConfig>("/app-config/");
    return response.data;
  }

  async getAgentConfigs(): Promise<AgentConfigResponse[]> {
    const client = await this.api.getClient();
    const response = await client.get<AgentConfigResponse[]>("/agents/configs");
    return response.data;
  }

  async createPlaidLinkToken(): Promise<{ linkToken: string }> {
    const client = await this.api.getClient();
    const response = await client.post<{ link_token: string }>(
      "/plaid/link-token",
    );
    return { linkToken: response.data.link_token };
  }

  async exchangePlaidPublicToken(
    publicToken: string,
    metadata: {
      institutionId?: string;
      institutionName?: string;
    },
  ): Promise<PlaidConnectionMessage[]> {
    const client = await this.api.getClient();
    const response = await client.post<PlaidConnectionMessage[]>(
      "/plaid/exchange",
      {
        public_token: publicToken,
        institution_id: metadata.institutionId,
        institution_name: metadata.institutionName,
      },
    );
    return response.data;
  }

  async mapPlaidConnection(
    connectionId: string,
    practiceId: string,
  ): Promise<PlaidConnectionMessage> {
    const client = await this.api.getClient();
    const response = await client.post<PlaidConnectionMessage>(
      `/plaid/connections/${connectionId}/map`,
      { practice_id: practiceId },
    );
    return response.data;
  }

  async getBankConnections(
    practiceIds: string[],
  ): Promise<PlaidConnectionMessage[]> {
    const client = await this.api.getClient();
    const response = await client.post<PlaidConnectionMessage[]>(
      "/credentials/bank",
      {
        filters: { practice_id: practiceIds },
        page: 1,
        page_size: 500,
      },
    );
    return response.data;
  }

  async deletePlaidConnection(connectionId: string): Promise<void> {
    const client = await this.api.getClient();
    await client.delete(`/plaid/connections/${connectionId}`);
  }

  async createPlaidReauthLinkToken(
    connectionId: string,
  ): Promise<{ linkToken: string }> {
    const client = await this.api.getClient();
    const response = await client.post<{ link_token: string }>(
      `/plaid/connections/${connectionId}/reauth-token`,
    );
    return { linkToken: response.data.link_token };
  }

  async completePlaidReauth(connectionId: string): Promise<void> {
    const client = await this.api.getClient();
    await client.post(`/plaid/connections/${connectionId}/reauth-complete`);
  }

  async skipPlaidConnection(connectionId: string): Promise<void> {
    const client = await this.api.getClient();
    await client.delete(`/plaid/connections/${connectionId}`);
  }

  async addAccountsLinkToken(
    connectionId: string,
  ): Promise<{ linkToken: string }> {
    const client = await this.api.getClient();
    const response = await client.post<{ link_token: string }>(
      `/plaid/connections/${connectionId}/add-accounts-token`,
    );
    return { linkToken: response.data.link_token };
  }

  async completeAddAccounts(
    connectionId: string,
  ): Promise<PlaidConnectionMessage[]> {
    const client = await this.api.getClient();
    const response = await client.post<PlaidConnectionMessage[]>(
      `/plaid/connections/${connectionId}/add-accounts-complete`,
    );
    return response.data;
  }

  async updateInsurance(
    request: UpdateInsuranceRequest,
  ): Promise<Record<string, string>> {
    const client = await this.api.getClient();
    const response = await client.put("/insurance", request);
    return response.data;
  }
}

export default QueryClient;
